import { useEffect, useState, useMemo } from "react";
import {
  subscribeOpenHouses,
  deleteOpenHouse,
  updateOpenHouse,
} from "../../utils/firebasefunctions";
import { deriveStatus, sortOpenHouses } from "../../utils/openHouseHelpers";
import { feedbackUrlFor } from "../../utils/openHouseEmails";
import { sendSchedulingEmails } from "../../utils/openHouseSend";
import OpenHousesTable from "./OpenHousesTable";
import ScheduleModal from "./ScheduleModal";
import CancelModal from "./CancelModal";
import FeedbackModal from "./FeedbackModal";
import RecapPreviewModal from "./RecapPreviewModal";
import RecapPendingBanner from "./RecapPendingBanner";
import { sendOpenHouseEmail } from "../../utils/emailSender";
import { composeInviteResendEmail } from "../../utils/openHouseEmailComposer";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";
import PageHeader from "../PageHeader";
import { SkeletonListItem } from "../Skeleton";

const FILTERS = ["all", "upcoming", "completed", "cancelled"];

export default function MainOpenHouses() {
  const [{ user, emailTemplates }] = useStateValue();
  const [allOpenHouses, setAllOpenHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  // Employees only see open houses they hosted. Admin/Supervisor see all.
  // Match by hostingAgentId (new) with a hostingAgentEmail fallback for OHs
  // that pre-date the assignment field.
  const isPrivileged = user?.role === "Admin" || user?.role === "Supervisor";
  const openHouses = useMemo(() => {
    if (isPrivileged) return allOpenHouses;
    return allOpenHouses.filter(
      (oh) =>
        oh.hostingAgentId === user?.id ||
        (oh.hostingAgentEmail &&
          user?.email &&
          oh.hostingAgentEmail.toLowerCase() === user.email.toLowerCase()),
    );
  }, [allOpenHouses, isPrivileged, user?.id, user?.email]);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [recapTarget, setRecapTarget] = useState(null);

  useEffect(() => {
    const unsub = subscribeOpenHouses((rows) => {
      setAllOpenHouses(rows);
      setLoading(false);
    });
    return () => unsub && unsub();
  }, []);

  const counts = useMemo(() => {
    const c = { total: openHouses.length, Scheduled: 0, Completed: 0, Cancelled: 0 };
    openHouses.forEach((oh) => {
      c[deriveStatus(oh)] = (c[deriveStatus(oh)] || 0) + 1;
    });
    return c;
  }, [openHouses]);

  const visibleRows = useMemo(() => {
    let rows = openHouses;
    if (filter === "upcoming") rows = rows.filter((oh) => deriveStatus(oh) === "Scheduled");
    else if (filter === "completed") rows = rows.filter((oh) => deriveStatus(oh) === "Completed");
    else if (filter === "cancelled") rows = rows.filter((oh) => deriveStatus(oh) === "Cancelled");
    return sortOpenHouses(rows);
  }, [openHouses, filter]);

  return (
    <div className="h-screen flex flex-col">
      <PageHeader
        title="Open Houses"
        subtitle={`${counts.total} open houses · ${counts.Scheduled || 0} upcoming`}
        actions={
          <button
            onClick={() => {
              setEditing(null);
              setScheduleOpen(true);
            }}
            className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold px-3 py-2 rounded-md"
          >
            + Schedule Open House
          </button>
        }
      />

      <div className="px-8 pt-4">
        <RecapPendingBanner openHouses={openHouses} onSent={() => {}} />
        <div className="inline-flex gap-1 bg-slate-100 rounded-lg p-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 text-xs font-semibold rounded-md capitalize ${
                filter === f ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="px-8 pt-4 grid grid-cols-4 gap-3">
        <StatCard label="Total" value={counts.total} />
        <StatCard label="Upcoming" value={counts.Scheduled || 0} color="text-blue-700" />
        <StatCard label="Completed" value={counts.Completed || 0} color="text-emerald-700" />
        <StatCard label="Cancelled" value={counts.Cancelled || 0} />
      </div>

      <div className="px-8 py-4 flex-1 overflow-y-auto">
        {loading ? (
          <div className="space-y-2 max-w-4xl">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-slate-200"
              >
                <SkeletonListItem />
              </div>
            ))}
          </div>
        ) : (
          <OpenHousesTable
            rows={visibleRows}
            onAddFeedback={setFeedbackTarget}
            onEditFeedback={setFeedbackTarget}
            onEdit={(oh) => {
              setEditing(oh);
              setScheduleOpen(true);
            }}
            onCancel={setCancelTarget}
            onSendRecap={(oh) => setRecapTarget(oh)}
            onSendInvites={async (oh) => {
              // Occupied-home flow: seller has now verbally OK'd the time,
              // so we release the hold and fire the original scheduling
              // emails. Confirm first to avoid an accidental click.
              const label =
                oh.propertyAddress?.split(",")[0] || "this open house";
              if (
                !window.confirm(
                  `Send the open-house notifications for ${label} to the seller and hosting agent now?`,
                )
              )
                return;
              try {
                await updateOpenHouse(
                  oh.id,
                  { pendingSellerApproval: false },
                  user.id,
                );
                await sendSchedulingEmails(oh.id, user, emailTemplates);
                toast.success("Notifications sent.");
              } catch (err) {
                console.error("Send invites failed:", err);
                toast.error("Could not send notifications. Try again.");
              }
            }}
            onResendInvite={async (oh) => {
              if (!oh.hostingAgentEmail) {
                toast.error("No hosting agent email on file.");
                return;
              }
              const { subject, text, html } = composeInviteResendEmail(oh, user, undefined, emailTemplates?.["scheduling-agent"]);
              const r = await sendOpenHouseEmail({
                userId: user.id,
                to: oh.hostingAgentEmail,
                toName: oh.hostingAgentName,
                subject, text, html,
                type: "invite-resend",
                relatedDealId: oh.dealId,
                relatedOpenHouseId: oh.id,
              });
              if (r.ok) toast.success("Invite re-sent.");
              else toast.error(`Re-send failed: ${r.error}`);
            }}
            onCopyLink={async (oh) => {
              const url = feedbackUrlFor(oh);
              try {
                await navigator.clipboard.writeText(url);
                toast.success("Feedback link copied to clipboard.");
              } catch {
                window.prompt("Copy this feedback link:", url);
              }
            }}
            onDelete={async (oh) => {
              const label = oh.propertyAddress?.split(",")[0] || "this open house";
              if (!window.confirm(`Delete ${label} permanently? This cannot be undone.`)) return;
              try {
                await deleteOpenHouse(oh.id);
                toast.success("Open house deleted.");
              } catch (err) {
                console.error("Delete failed:", err);
                toast.error("Could not delete. Try again.");
              }
            }}
          />
        )}
      </div>

      <ScheduleModal
        open={scheduleOpen}
        existing={editing}
        onClose={() => setScheduleOpen(false)}
      />
      <CancelModal
        open={!!cancelTarget}
        openHouse={cancelTarget}
        onClose={() => setCancelTarget(null)}
      />
      <FeedbackModal
        open={!!feedbackTarget}
        openHouse={feedbackTarget}
        onClose={() => setFeedbackTarget(null)}
        onSaved={(updated) => {
          setFeedbackTarget(null);
          setRecapTarget(updated);
        }}
      />
      <RecapPreviewModal
        open={!!recapTarget}
        openHouse={recapTarget}
        onClose={() => setRecapTarget(null)}
      />
    </div>
  );
}

function StatCard({ label, value, color = "text-slate-900" }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
